import React, { useMemo, useEffect, useState, memo, FC, useReducer } from "react";
import {
    Button,
    Card,
    Grid,
    List,
    ListItem,
    Tooltip,
} from "@mui/material";
import LotCard from "@/components/ui/LotCard";
import QuickBidPopover from "@/components/ui/QuickBidPopover";
import LotDetailCardDocumentSelector from "@/components/Cards/LotDetailCardDocumentSelector";
import { AuctionTypeEnum } from "@/lib/utils/AuctionTypeEnum";
import { AuctionLotStatusEnum } from "@/lib/utils/AuctionLotStatusEnum";
import { connect, useSelector } from "react-redux";
import { PricingDisplay } from "@/components/library/PricingDisplay";
import { getHighestBidColour, getHighestBidSuffix, insertUrlParam } from '@/lib/utils/General';
import { useConfigureSoldBanner } from "@/lib/hooks/useConfigureSoldBanner";
import useHistory from "../../lib/hooks/useHistory";
import { RootState } from "../../store/Store";
import { getNewDateWithOffset } from "prembid-shared-library-npm/helpers";
import { onReceiveMessage, clearOnReceiveMessage } from "prembid-shared-library-npm/signalr";
import { MessageSubject } from "prembid-shared-library-npm/types";
import { useFinalizing, useConfigureCounter, useParticipateStatus } from "prembid-shared-library-npm/hooks";
import { LotStatus } from "../../lib/utils/LotStatusEnum";
import { BiddingTable } from "../containers/BiddingTable";
import { MyActivityWishlist } from "../containers/MyActivity";
import { GeneralPopper } from "../composite/popper/GeneralPopper";
import { PrembidLocalStorage } from '@/lib/utils/PrembidLocalStorage'

export interface LotDetailCardProps {
    imgSrc: string;
    displayFullLengthImage: boolean;
    header: string;
    description?: string;
    lotFeatures?: any;
    auctionId: string;
    lotId: string;
    commission: number;
    lotNumber: string;
    auctionType: number;
    profileId: string,

    onRefresh?: () => void;

    startDateTimeAt?: any;
    endDateTimeAt?: any;

    successBadgeText?: string;
    startingPrice?: number;
    dangerBadgeText?: string;
    guidePrice?: any;

    hasExtensions: boolean;
    isActive: boolean;
    documentCount: any;

    registrationStatus: any;
    wishListStatus: boolean;
    overrideGuidePriceRulesAndAlwaysHide: boolean;
    overrideShowBidButtonForCompletedLots: boolean;
    settings: any;
    showReserve: boolean;
    highestBid: number;
    highestBidProfileId: string | null;
    extendedBy: number;
    lot: any
}

const LotDetailCard: FC<LotDetailCardProps> = (props) => {

    const {
        imgSrc,
        displayFullLengthImage,
        header,
        description,
        lotFeatures,
        auctionId,
        lotId,
        commission,
        lotNumber,
        auctionType,
        profileId,
        startDateTimeAt,
        endDateTimeAt,
        successBadgeText,
        guidePrice,
        hasExtensions,
        isActive,
        documentCount,
        registrationStatus,
        wishListStatus,
        overrideGuidePriceRulesAndAlwaysHide,
        overrideShowBidButtonForCompletedLots,
        settings,
        showReserve,
        highestBid,
        highestBidProfileId,
        extendedBy,
        lot,
        startingPrice
    } = props;

    const currentDateTimeOffset = useSelector((state: RootState) => state.settings.serverDateTimeDifference);
    const { participateStatus, invalidateParticipateStatus, loading } = useParticipateStatus({
        profileId,
        auctionId
    });

    const history = useHistory();

    const now = getNewDateWithOffset(currentDateTimeOffset);
    const start = useMemo(() => new Date(startDateTimeAt), [startDateTimeAt]);

    const [infoAnchorEl, setInfoAnchorEl] = useState(null);
    const [quickBidAnchorEl, setQuickBidAnchorEl] = useState(null);

    const [end, setEnd] = useState<Date>();

    const reserveMet = (reservePrice: number, highestBid: number) => {
        const tempReservePrice = reservePrice ? Number(reservePrice) : 0;
        return tempReservePrice > 0 ? Number(highestBid) >= tempReservePrice : true;
    };

    const { finalizingText, setValidating, validating } = useFinalizing({
        auctionType,
        lotLocale: settings.LotName,
    });

    const [liveUpdates, setLiveUpdates] = useState({
        highestBid: highestBid,
        highestBidProfileId: lot?.winningProfileId,
        isExtended: hasExtensions,
        reserveMet: reserveMet(lot?.reservePrice, lot?.overrideWinningBidAmount ?? lot?.winningRegisteredProfileAmount),
        reservePrice: lot?.reservePrice,
        startingPrice,
        extendedBy: extendedBy,
    });

    useEffect(() => {
        setLiveUpdates((current) => ({
            ...current,
            highestBid: highestBid,
            highestBidProfileId: lot?.winningProfileId,
            reserveMet: reserveMet(lot?.reservePrice, lot?.overrideWinningBidAmount ?? lot?.winningRegisteredProfileAmount),
            isExtended: hasExtensions,
            reservePrice: lot?.reservePrice,
            startingPrice,
            extendedBy: extendedBy,
        }));
    }, [hasExtensions, highestBid, lot?.winningProfileId, highestBidProfileId, lot?.overrideWinningBidAmount, lot?.reservePrice, lot?.winningRegisteredProfileAmount, startingPrice, extendedBy])

    useEffect(() => {
        if (!endDateTimeAt) return;
        const endTime = new Date(endDateTimeAt)
        if (liveUpdates?.extendedBy) endTime.setTime(endTime.getTime() + ((liveUpdates.extendedBy ?? extendedBy ?? 0) * 1000))
        setEnd(new Date(endTime));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endDateTimeAt, liveUpdates?.extendedBy]);

    const { countdownDate, countdownHeader, status, previousStatus } = useConfigureCounter({
        start,
        end,
    });

    useEffect(() => {
        if (previousStatus !== undefined && status === LotStatus.COMPLETED && !lot?.sold) {
            setValidating(true);
            setTimeout(() => {
                setValidating(false)
            }, 3000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    const { soldBanner, handleShowSoldBanner } = useConfigureSoldBanner(
        lot,
        liveUpdates
    );

    useEffect(() => {
        handleShowSoldBanner(lot, liveUpdates, status);
    }, [handleShowSoldBanner, liveUpdates, lot, status]);

    useEffect(() => {
        const callbackId = onReceiveMessage((message: string) => {
            if (message) {
                var payload = JSON.parse(message);

                if (payload?.data) {
                    const messageSubject = payload.data['messageSubject'];
                    const messageAuctionId = payload.data['auctionId'];
                    const messageLotId = payload.data['lotId'];
                    const messageAmount = payload.data['amount'];
                    const messageProfileId = payload.data['profileId'];
                    const messageExtendedBy = payload.data['extendedBy'];

                    if (messageSubject === MessageSubject.UpdateRegisteredProfile && messageAuctionId === auctionId && messageProfileId === PrembidLocalStorage.currentProfile.toString()) {
                        invalidateParticipateStatus();
                    }

                    if (messageLotId === lotId) {
                        if (messageSubject === MessageSubject.BidUpdated) {
                            const winningRegisteredProfileAmount = payload.data['winningRegisteredProfileAmount'];
                            const winningProfileId = payload.data['winningProfileId'];

                            setLiveUpdates(current => ({
                                ...current,
                                highestBid: winningRegisteredProfileAmount,
                                highestBidProfileId: winningProfileId,
                                reserveMet: reserveMet(current?.reservePrice, winningRegisteredProfileAmount),
                            }));
                        }
                    }

                    if (messageAuctionId === auctionId && messageLotId === lotId) {
                        if (messageSubject === MessageSubject.CreateBid) {
                            setLiveUpdates(current => ({
                                ...current,
                                highestBid: messageAmount,
                                highestBidProfileId: messageProfileId,
                                reserveMet: reserveMet(current?.reservePrice, messageAmount),
                            }));

                            if (messageExtendedBy !== "" && messageExtendedBy !== undefined) {
                                setLiveUpdates(current => ({
                                    ...current,
                                    isExtended: true,
                                    extendedBy: messageExtendedBy,
                                }));
                            }
                        }

                        if (messageSubject === MessageSubject.UpdatedStartPrice) {
                            setLiveUpdates((current) => ({
                                ...current,
                                startingPrice: payload.data['startingPrice'],
                            }));
                        }

                        if (messageSubject === MessageSubject.UpdatedReservePrice) {
                            setLiveUpdates((current) => ({
                                ...current,
                                reservePrice: Number(payload.data["reservePrice"]),
                                reserveMet: reserveMet(payload.data["reservePrice"], current?.highestBid),
                            }));
                        }

                        if (messageSubject === MessageSubject.ManuallyExtendedLot) {
                            setLiveUpdates((current) => ({
                                ...current,
                                isExtended: true,
                                extendedBy: messageExtendedBy,
                            }));
                        }
                    }
                }
            }
        });
        return () => clearOnReceiveMessage(callbackId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const recordScrollPosition = () => {
        var top = window.scrollY;
        insertUrlParam("top", top.toString());
    };

    const btnViewLotDetailsRoute = () => {
        recordScrollPosition();
        history.push("/LotBidAndInfo/" + auctionId + "/" + lotId);
    };

    const btnBidRoute = () => {
        recordScrollPosition();
        history.push("/LotBidAndInfo/" + auctionId + "/" + lotId);
    };

    const handleInfoPopperClick = (event) => {
        setInfoAnchorEl(event.currentTarget);
    };

    const handleInfoPopperClose = () => {
        setInfoAnchorEl(null);
    };

    const handleQuickBidPopperClick = (event) => {
        setQuickBidAnchorEl(event.currentTarget);
    };

    const handleQuickBidPopperClose = () => {
        setQuickBidAnchorEl(null);
    };

    const getInfoElement = () => {
        const placeholderText = Number(auctionType) === Number(AuctionTypeEnum.Tender) ? "tender" : "bid";
        const placeholderActioningText = Number(auctionType) === Number(AuctionTypeEnum.Tender) ? "tendering" : "bidding";

        let lotStatus = AuctionLotStatusEnum["Unavailable"];

        if (!isActive) lotStatus = AuctionLotStatusEnum["Unavailable"];

        if (now < start) lotStatus = AuctionLotStatusEnum.Awaiting;

        if (!!end && now > end) lotStatus = AuctionLotStatusEnum.Completed;

        if (!!end && start < now && now < end) {
            if (liveUpdates?.isExtended) {
                lotStatus = AuctionLotStatusEnum.Extended;
            } else {
                lotStatus = AuctionLotStatusEnum.Open;
            }
        }

        if (validating) lotStatus = AuctionLotStatusEnum.Validating;

        return (
            <List>
                <ListItem>
                    <h5 className="text-primary font-weight-bold">
                        {lotStatus !== undefined && <>{AuctionLotStatusEnum[lotStatus.toString()]} -{" "}</>}
                        {lotStatus === AuctionLotStatusEnum["Unavailable"] && (
                            <span className="text-black font-weight-normal">
                                {"This " +
                                    settings.LotName.toLowerCase() +
                                    " is no longer available and " +
                                    placeholderText +
                                    "s cannot be submitted on it"}
                            </span>
                        )}
                        {lotStatus === AuctionLotStatusEnum.Awaiting && (
                            <span className="text-black font-weight-normal">
                                {"This " +
                                    settings.LotName.toLowerCase() +
                                    " hasn't opened yet, but you need to register if you want to " +
                                    placeholderText +
                                    " when it does"}
                            </span>
                        )}
                        {lotStatus === AuctionLotStatusEnum.Open && (
                            <span className="text-black font-weight-normal">
                                {"This " +
                                    settings.LotName.toLowerCase() +
                                    " is open for you to " +
                                    placeholderText +
                                    " on " +
                                    "if you have registered and been approved"}
                            </span>
                        )}
                        {lotStatus === AuctionLotStatusEnum.Extended && (
                            <span className="text-black font-weight-normal">
                                {"This " +
                                    settings.LotName.toLowerCase() +
                                    " has been extended due to " +
                                    placeholderActioningText +
                                    " activity, " +
                                    placeholderActioningText +
                                    " is still open on certain " +
                                    settings.LotName.toLowerCase() +
                                    "s"}
                            </span>
                        )}
                        {lotStatus === AuctionLotStatusEnum.Completed && (
                            <span className="text-black font-weight-normal">
                                {"This " +
                                    settings.LotName.toLowerCase() +
                                    " has completed and " +
                                    placeholderActioningText +
                                    " is closed"}
                            </span>
                        )}
                        {lotStatus === AuctionLotStatusEnum.Validating && (
                            <span className="text-black font-weight-normal">
                                {"Validating the status of the " + settings.LotName.toLowerCase()}
                            </span>
                        )}
                    </h5>
                </ListItem>
                <ListItem>
                    <h5 className="text-primary font-weight-bold">
                        STC -{" "}
                        <span className="text-black font-weight-normal">
                            {"Subject to Confirmation, conditions need to be met in order for the " +
                                settings.LotName.toLowerCase() +
                                " to be sold"}
                        </span>
                    </h5>
                </ListItem>
                <ListItem>
                    <h5 className="text-primary font-weight-bold">
                        Buyers Commission -{" "}
                        <span className="text-black font-weight-normal">
                            {"Commission payable by the buyer on top of the purchase price"}
                        </span>
                    </h5>
                </ListItem>
                <ListItem>
                    <h5 className="text-primary font-weight-bold">
                        Reserve -{" "}
                        <span className="text-black font-weight-normal">
                            {"Has the minimum price for the " +
                                settings.LotName.toLowerCase() +
                                " been met?"}
                        </span>
                    </h5>
                </ListItem>
            </List>
        );
    };


    // Show the user's own accepted quick / custom bid on the card right away, as the bidding page does (and as on mobile);
    // the live update that follows confirms it, or replaces it if someone outbid them in the meantime.
    const handleBidPlaced = (amount: number) => {
        setLiveUpdates((current: any) =>
            amount > Number(current?.highestBid ?? 0)
                ? { ...current, highestBid: amount, highestBidProfileId: PrembidLocalStorage.currentProfile, reserveMet: reserveMet(current?.reservePrice, amount) }
                : current
        );
    };

    const isTender = Number(auctionType) === Number(AuctionTypeEnum.Tender.toString());
    const isStreamed = Number(auctionType) === Number(AuctionTypeEnum.Streamed.toString());

    return (
        <Grid item xs={12}>
            <LotCard
                variant="detail"
                imgSrc={imgSrc}
                onOpen={btnViewLotDetailsRoute}
                lotNumber={lotNumber}
                title={header}
                description={description}
                lotFeatures={lotFeatures}
                isActive={isActive}
                validating={validating}
                finalizingText={finalizingText}
                soldBannerText={soldBanner?.showBanner === true ? soldBanner?.bannerText : undefined}
                now={now}
                start={start}
                end={end}
                isExtended={liveUpdates?.isExtended}
                stcStatus={successBadgeText}
                commission={commission}
                showReserve={showReserve}
                reserveMet={liveUpdates?.reserveMet}
                countdownDate={countdownDate}
                countdownHeader={countdownHeader}
                getNow={() => getNewDateWithOffset(currentDateTimeOffset).getTime()}
                documents={<LotDetailCardDocumentSelector auctionId={auctionId} lotId={lotId} documentCount={documentCount} />}
                wishlist={<MyActivityWishlist lotId={lotId} isOnWishlist={wishListStatus} utilityClasses="pb-wish" size={22} />}
                onInfoClick={handleInfoPopperClick}
                infoPopper={infoAnchorEl && <GeneralPopper headerText={"Info"} anchorEl={infoAnchorEl} element={getInfoElement()} handleGeneralPopperClose={handleInfoPopperClose} />}
                pricing={
                    <PricingDisplay
                        variant="detail"
                        auctionType={auctionType}
                        guidePrice={guidePrice?.toString()}
                        overrideGuidePriceRulesAndAlwaysHide={overrideGuidePriceRulesAndAlwaysHide}
                        registrationStatus={participateStatus?.status ?? registrationStatus}
                        openingBid={liveUpdates?.startingPrice ?? 0}
                        highestBid={liveUpdates?.highestBid}
                        highestBidColour={getHighestBidColour(liveUpdates?.highestBidProfileId)}
                        highestBidSuffix={getHighestBidSuffix(liveUpdates?.highestBidProfileId)}
                    />
                }
                actions={isActive && (
                    <>
                        <div>
                            <button type="button" className="pb-btn pb-btn-primary" onClick={btnViewLotDetailsRoute}>
                                {isStreamed ? "Info" : isTender ? "Tender/Info" : "Bid/Info"}
                            </button>
                        </div>
                        {!isStreamed && (
                            <div>
                                <button type="button" className="pb-btn pb-btn-ink" aria-haspopup="true" onClick={(e: any) => setQuickBidAnchorEl(quickBidAnchorEl ? null : e.currentTarget)}>
                                    {isTender ? "Quick Tender" : "Quick Bid"}
                                </button>
                                <QuickBidPopover onBidPlaced={handleBidPlaced} anchorEl={quickBidAnchorEl} onClose={() => setQuickBidAnchorEl(null)} auctionId={auctionId} lotId={lotId} isTender={isTender} />
                            </div>
                        )}
                    </>
                )}
            />
        </Grid>
    );
};

const mapDispatchToProps = (dispatch: any) => {
    return {};
};

const mapStateToProps = (state: any) => ({
    settings: state.settings.settings,
    profileId: state.loginForm.changeProfileId,
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(LotDetailCard));
